import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  addCartItem,
  applyCoupon as applyCouponApi,
  getCart,
  removeCartItem,
  removeCoupon as removeCouponApi,
  updateCartItem,
  type Cart,
  type CartItemPayload,
} from "./api-client";

/**
 * Panier — état partagé TanStack Query + mutations. Porté depuis
 * apps/web/lib/cart-actions.ts (Server Actions) et
 * apps/web/lib/api/cart-summary.ts, fusionnés ici en un seul module puisque
 * ce SPA n'a plus de distinction Server Component (lecture) / Server Action
 * (écriture) : tout passe par TanStack Query, lecture (`useCart`) et écriture
 * (`useAddToCart`/`useSetCartItemQuantity`/`useRemoveCartItem`/
 * `useApplyCoupon`/`useRemoveCoupon`) confondues.
 *
 * Jeton de panier invité (`vvs_session_token`) : AUCUNE gestion manuelle
 * nécessaire ici. Confirmé par lecture directe de la source de vérité
 * (apps/api/src/common/cart-context/cart-context.ts `writeSessionToken()`,
 * appelée par `CartController.respond()` sur CHAQUE mutation) : l'API pose
 * déjà elle-même un cookie httpOnly `vvs_session_token` (même mécanisme que
 * `vvs_access_token`/`vvs_admin_access_token`, cf.
 * apps/api/src/common/auth/auth-cookies.util.ts) en plus de le renvoyer dans
 * `Cart.sessionToken` (JSON) et l'en-tête `X-Session-Token` — ces deux
 * dernières formes existent pour un client qui ne gère pas les cookies
 * (mobile natif, etc.), pas pour ce SPA. `credentials: "include"`
 * (api-client.ts) suffit donc : le navigateur attache et rafraîchit ce cookie
 * tout seul, exactement comme pour l'auth client/admin. Toutes les fonctions
 * ci-dessous appellent `api-client.ts` SANS second argument `ctx` — le
 * paramètre `CartAuthContext` explicite qui y subsiste ne sert qu'à garder la
 * signature portée telle quelle (cf. commentaire de tête de api-client.ts),
 * plus aucun appelant de ce lot n'a de jeton en main à lui passer.
 */

export const CART_QUERY_KEY = ["cart"] as const;

/** Message d'erreur lisible, porté depuis apps/web/lib/cart-actions.ts `describeError()`. */
export function describeCartError(err: unknown): string {
  if (err instanceof ApiError) {
    return err.problem?.detail ?? err.problem?.title ?? err.message;
  }
  return "Une erreur est survenue. Merci de réessayer.";
}

/**
 * `null` = panier vide OU API indisponible, délibérément non distingués ici —
 * même choix que l'original (`getCart(ctx).catch(() => null)` dans
 * `apps/web/app/(shop)/panier/page.tsx`) : un panier qui ne charge pas se
 * comporte comme un panier vide, jamais comme un écran d'erreur bloquant.
 */
async function fetchCartOrNull(): Promise<Cart | null> {
  try {
    return await getCart();
  } catch {
    return null;
  }
}

export function useCart() {
  return useQuery({ queryKey: CART_QUERY_KEY, queryFn: fetchCartOrNull });
}

/**
 * Ne fait jamais échouer l'appelant : renvoie `0` si l'API est indisponible,
 * mal configurée, ou renvoie une erreur — le badge panier de l'en-tête ne
 * doit jamais faire échouer le rendu de la page. Partage la même
 * `queryKey` que `useCart()` (`CART_QUERY_KEY`) : un `invalidateQueries` après
 * n'importe quelle mutation panier met donc aussi à jour ce compteur, sans
 * requête séparée.
 */
export function useCartItemCount(): number {
  const { data } = useCart();
  return data?.itemCount ?? 0;
}

/**
 * Fabrique commune à toutes les mutations panier ci-dessous : écrit
 * optimistiquement le `Cart` renvoyé par l'API dans le cache (évite un aller-
 * retour réseau supplémentaire le temps de l'invalidation), PUIS invalide
 * `CART_QUERY_KEY` dans tous les cas (succès ou échec, `onSettled`) —
 * reproduit `revalidateCartPaths()` appelé inconditionnellement après chaque
 * action dans apps/web/lib/cart-actions.ts, y compris sur erreur (une requête
 * en échec peut malgré tout avoir changé l'état serveur, ex. stock épuisé
 * entre-temps).
 */
function useCartMutation<TVariables>(mutationFn: (vars: TVariables) => Promise<Cart>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (cart) => {
      queryClient.setQueryData(CART_QUERY_KEY, cart);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY });
    },
  });
}

/**
 * Ajoute (ou cumule) une ligne — cf. `addCartItemAction` (apps/web). Hook
 * RÉUTILISABLE : c'est le point d'entrée que toute page doit utiliser pour
 * ajouter un article au panier (fiche produit, carrousel de la page panier,
 * carte produit du catalogue…), plutôt que d'appeler `addCartItem()`
 * (api-client.ts) directement — la synchro TanStack Query (cache + badge
 * d'en-tête) est centralisée ici.
 *
 * Usage : `const addToCart = useAddToCart(); addToCart.mutate({ variantId, quantity })`.
 */
export function useAddToCart() {
  return useCartMutation<CartItemPayload>((payload) => addCartItem(payload));
}

/** Boutons [−]/[+] d'une ligne du panier (§7.2) : fixe la quantité absolue. */
export function useSetCartItemQuantity() {
  return useCartMutation<CartItemPayload>((payload) => updateCartItem(payload));
}

/** Bouton ✕ d'une ligne du panier. */
export function useRemoveCartItem() {
  return useCartMutation<string>((variantId) => removeCartItem(variantId));
}

/** Formulaire code promo (§7.2). */
export function useApplyCoupon() {
  return useCartMutation<string>((code) => applyCouponApi(code));
}

export function useRemoveCoupon() {
  return useCartMutation<void>(() => removeCouponApi());
}
